import { OktonInput, OktonTextarea } from '@okton/platform-ui';
import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  Heading,
  IconButton,
  useColorModeValue,
  useToast,
  Badge,
  Icon,
  HStack,
  Image,
} from '@chakra-ui/react';
import * as z from 'zod';
import {
  Controller,
  FormProvider,
  useForm,
  useFieldArray,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FiArrowLeft, FiCheck, FiTrash2, FiFileText } from 'react-icons/fi';
import { useHistory, useLocation } from 'react-router-dom';
import { Column } from 'react-table';
import type { AxiosError } from 'axios';
import { OktonReactTable as Table } from '@okton/platform-ui/react-table';

import backend from '../../services/backend';
import Page from '../../layout/IntegraAppPage';
import { queryClient } from '../../services/queryClient';

const produtoSchema = z.object({
  id: z.string(),
  imagens: z
    .array(
      z.object({
        url: z.string().url(),
      }),
    )
    .optional()
    .nullable(),
  sku: z.string().min(1, 'SKU obrigatório'),
  titulo: z.string().min(1, 'Título obrigatório'),
  preco: z.coerce.number().min(0),
  estoqueTotal: z.coerce.number().min(0),
  descricao: z.string().optional().default(''),
});

const formSchema = z.object({
  produtos: z.array(produtoSchema),
});

type ProdutoForm = z.infer<typeof produtoSchema>;
type FormType = z.infer<typeof formSchema>;
type ProdutoImportado = {
  id?: string;
  imagens?: ProdutoForm['imagens'];
  sku?: string;
  titulo?: string;
  preco?: number | string;
  estoqueTotal?: number | string;
  descricao?: string;
};
type ProdutoRow = {
  row: {
    index: number;
    original: ProdutoForm;
  };
};

const RevisarImportacao: React.FC = () => {
  const history = useHistory();
  const location = useLocation<{ produtosImportados: ProdutoImportado[] }>();
  const toast = useToast();

  const corDeFundo = useColorModeValue('white', 'gray.800');
  const corDaBorda = useColorModeValue('gray.200', 'gray.700');
  const corSec = useColorModeValue('gray.500', 'gray.400');

  const produtosIniciais =
    location.state?.produtosImportados?.map(p => ({
      id: p.id || crypto.randomUUID(),
      imagens: p.imagens || null,
      sku: p.sku || '',
      titulo: p.titulo || '',
      preco: Number(p.preco || 0),
      estoqueTotal: Number(p.estoqueTotal || 0),
      descricao: p.descricao || '',
    })) || [];

  const methods = useForm<FormType>({
    defaultValues: {
      produtos: produtosIniciais,
    },
    resolver: zodResolver(formSchema),
  });

  const {
    control,
    getValues,
    watch,
    reset,
    formState: { isSubmitting },
  } = methods;

  watch('produtos');

  const { fields } = useFieldArray({
    control,
    name: 'produtos',
  });

  const salvarImportacao = async () => {
    const produtos = getValues('produtos');

    if (!produtos.length) return;

    const validos: ProdutoForm[] = [];
    const invalidos: ProdutoForm[] = [];

    produtos.forEach(prod => {
      const resultado = produtoSchema.safeParse(prod);

      if (resultado.success) {
        validos.push(resultado.data);
      } else {
        invalidos.push(prod);
      }
    });

    if (validos.length === 0) {
      reset({ produtos: invalidos });

      toast({
        title: 'Nenhum produto válido',
        description: 'Corrija os produtos inválidos e tente novamente.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });

      return;
    }

    try {
      await backend.post('/produtos/completo', {
        produtos: validos,
      });

      await queryClient.invalidateQueries('produtos');

      history.push('/produtos');

      if (invalidos.length > 0) {
        reset({
          produtos: invalidos,
        });

        toast({
          title: 'Importação Parcial',
          description: `${validos.length} produtos salvos e ${invalidos.length} com erro.`,
          status: 'warning',
          duration: 8000,
          isClosable: true,
        });

        return;
      }

      toast({
        title: 'Sucesso!',
        description: `${validos.length} produtos importados.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      history.push('/produtos');
    } catch (error) {
      const err = error as AxiosError<{ message?: string }>;
      toast({
        title: 'Erro ao salvar',
        description:
          err.response?.data?.message ||
          'Ocorreu um erro ao salvar os produtos.',
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
    }
  };

  const columns = useMemo<Array<Column<ProdutoForm>>>(
    () => [
      {
        Header: 'IMG',
        accessor: 'imagens',
        Cell: ({ row }: ProdutoRow) => (
          <Box
            w="40px"
            h="40px"
            bg="gray.50"
            borderRadius="md"
            overflow="hidden"
            border="1px solid"
            borderColor="gray.100"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {row.original.imagens ? (
              <Image
                src={row.original.imagens?.[0]?.url}
                boxSize="40px"
                objectFit="cover"
              />
            ) : (
              <Icon as={FiFileText} color="gray.300" />
            )}
          </Box>
        ),
      },
      {
        Header: 'SKU',
        accessor: 'sku',
        Cell: ({ row }: ProdutoRow) => (
          <Controller
            control={control}
            name={`produtos.${row.index}.sku`}
            render={({ field }) => (
              <OktonInput
                name={`produtos.${row.index}.sku`}
                value={field.value}
                variant="unstyled"
                p={2}
                onChange={field.onChange}
              />
            )}
          />
        ),
      },
      {
        Header: 'PRODUTO',
        accessor: 'titulo',
        Cell: ({ row }: ProdutoRow) => (
          <Controller
            control={control}
            name={`produtos.${row.index}.titulo`}
            render={({ field }) => (
              <OktonInput
                name={`produtos.${row.index}.titulo`}
                value={field.value}
                variant="unstyled"
                p={2}
                onChange={field.onChange}
              />
            )}
          />
        ),
      },
      {
        Header: 'DESCRIÇÃO',
        accessor: 'descricao',
        Cell: ({ row }: ProdutoRow) => (
          <Controller
            control={control}
            name={`produtos.${row.index}.descricao`}
            render={({ field }) => (
              <OktonTextarea
                name={`produtos.${row.index}.descricao`}
                value={field.value}
                variant="unstyled"
                p={2}
                resize="none"
                onChange={field.onChange}
              />
            )}
          />
        ),
      },
      {
        Header: 'PREÇO',
        accessor: 'preco',
        Cell: ({ row }: ProdutoRow) => (
          <Controller
            control={control}
            name={`produtos.${row.index}.preco`}
            render={({ field }) => (
              <OktonInput
                name={`produtos.${row.index}.preco`}
                value={field.value}
                type="number"
                variant="unstyled"
                p={2}
                onChange={e => field.onChange(Number(e.target.value))}
              />
            )}
          />
        ),
      },
      {
        Header: 'ESTOQUE',
        accessor: 'estoqueTotal',
        Cell: ({ row }: ProdutoRow) => (
          <Controller
            control={control}
            name={`produtos.${row.index}.estoqueTotal`}
            render={({ field }) => (
              <OktonInput
                name={`produtos.${row.index}.estoqueTotal`}
                value={field.value}
                type="number"
                variant="unstyled"
                p={2}
                onChange={e => field.onChange(Number(e.target.value))}
              />
            )}
          />
        ),
      },
      {
        Header: 'AÇÕES',
        id: 'actions',
        Cell: ({ row }: ProdutoRow) => (
          <IconButton
            aria-label="Remover"
            icon={<FiTrash2 />}
            size="xs"
            variant="ghost"
            colorScheme="red"
            onClick={() => {
              const produtos = getValues('produtos');
              const novos = produtos.filter(
                item => item.id !== row.original.id,
              );

              reset({
                produtos: novos,
              });
            }}
          />
        ),
      },
    ],
    [control, getValues, reset],
  );

  return (
    <Page>
      <FormProvider {...methods}>
        <form
          onSubmit={e => {
            e.preventDefault();
            salvarImportacao();
          }}
        >
          <Box mb={6}>
            <Flex justify="space-between" align="center">
              <HStack spacing={4}>
                <IconButton
                  aria-label="Voltar"
                  icon={<FiArrowLeft />}
                  onClick={() => history.goBack()}
                  variant="ghost"
                />

                <Box>
                  <Heading size="lg">Revisar Importação</Heading>
                  <Text fontSize="sm" color={corSec}>
                    Corrija apenas os itens pendentes
                  </Text>
                </Box>
              </HStack>

              <Button
                type="submit"
                bg="blue.500"
                color="white"
                leftIcon={<FiCheck />}
                isLoading={isSubmitting}
              >
                Salvar Tudo
              </Button>
            </Flex>
          </Box>

          <Box
            bg={corDeFundo}
            border="1px solid"
            borderColor={corDaBorda}
            borderRadius="2xl"
            overflow="hidden"
          >
            <Box p={6}>
              <Flex justify="space-between">
                <Text fontWeight="bold">Produtos Pendentes</Text>

                <Badge colorScheme="orange">{fields.length} ITENS</Badge>
              </Flex>
            </Box>

            <Box p={4}>
              <Table columns={columns} data={fields} />
            </Box>
          </Box>
        </form>
      </FormProvider>
    </Page>
  );
};

export default RevisarImportacao;
